import { slugify } from "../strings";

export const HOME_BUYERS_DIRECTOR_EMAIL = "brobertson@fergmar.com";
export const HOME_BUYERS_DIRECTOR_TITLE = "Director, Acquisitions";
export const HOME_BUYERS_TEAM_PATH = "/meet-the-team";
export const HOME_BUYERS_ASSOCIATE_PAGES_ENABLED = false;

export interface HomeBuyersTeamMember {
	displayName: string;
	title: string;
	headshot: string;
	workEmail: string;
	workNumber: string | null;
	slug: string;
	hasPage: boolean;
}

export interface HomeBuyersTeamSource {
	displayName: string;
	title: string;
	headshot: string;
	workEmail: string;
	workNumber: string | null;
	status: string;
	hireDate: string;
}

function normalizeWorkNumber(value: string | null): string | null {
	const trimmed = value?.trim() ?? "";
	if (trimmed === "" || trimmed.toLowerCase() === "null") return null;
	return trimmed;
}

function isDirector(member: HomeBuyersTeamSource): boolean {
	return member.workEmail.trim().toLowerCase() === HOME_BUYERS_DIRECTOR_EMAIL;
}

function rank(member: HomeBuyersTeamSource): number {
	if (isDirector(member)) return 0;
	return member.title.toLowerCase().includes("manager") ? 1 : 2;
}

export function buildHomeBuyersTeam(
	members: HomeBuyersTeamSource[],
	{
		associatePages = HOME_BUYERS_ASSOCIATE_PAGES_ENABLED,
		onSlugCollision = () => {}
	}: {
		associatePages?: boolean;
		onSlugCollision?: (slug: string, displayName: string) => void;
	} = {}
): HomeBuyersTeamMember[] {
	const ordered = members
		.filter(
			(member) =>
				member.status === "Active" &&
				(isDirector(member) || member.title.toLowerCase().includes("acquisition"))
		)
		.sort(
			(a, b) =>
				rank(a) - rank(b) ||
				a.hireDate.localeCompare(b.hireDate) ||
				a.workEmail.localeCompare(b.workEmail)
		);

	const takenSlugs = new Set<string>();
	return ordered.map((member) => {
		const director = isDirector(member);
		const slug = slugify(member.displayName);
		let hasPage = associatePages && !director && slug !== "";
		if (hasPage && takenSlugs.has(slug)) {
			hasPage = false;
			onSlugCollision(slug, member.displayName);
		}
		if (hasPage) takenSlugs.add(slug);
		return {
			displayName: member.displayName,
			title: director ? HOME_BUYERS_DIRECTOR_TITLE : member.title,
			headshot: member.headshot,
			workEmail: member.workEmail,
			workNumber: normalizeWorkNumber(member.workNumber),
			slug,
			hasPage
		};
	});
}

export function findAssociateBySlug(
	team: HomeBuyersTeamMember[],
	slug: string
): HomeBuyersTeamMember | null {
	return team.find((entry) => entry.hasPage && entry.slug === slug.toLowerCase()) ?? null;
}

export function phoneHref(phone: string): string {
	return `tel:${phone.replace(/\D/g, "")}`;
}
